import { activeBenefitsAtom } from "@/atoms/active-benefits";
import { RailScroller } from "@/components/home/RailScroller";
import { RAIL_COLUMN, SectionRail } from "@/components/home/SectionRail";
import { BenefitCard, BenefitSpotlight } from "@/components/home/BenefitCard";
import { Text } from "@/components/ui";
import {
  BENEFIT_WIDTH_RATIO,
  RAIL_MICRO_REM,
  STOREFRONT_SECTION_LABELS,
  type StorefrontSection,
} from "@/domain/storefront-appearance";
import { useMediaQuery } from "@/hooks/use-media-query";
import {
  buildBenefitShowcase,
  stackingRuleCopy,
  type BenefitShowcaseCard,
} from "@/lib/benefit-showcase";
import { useReducedMotion } from "motion/react";
import { useAtomValue } from "jotai";
import { useCallback, useMemo, useRef, useState } from "react";

/** Consecutive still frames that mark a smooth scroll as finished. */
const SCROLL_SETTLE_FRAMES = 3;

/** Longest wait for the centring scroll before the card opens anyway. */
const SCROLL_SETTLE_MAX_MS = 700;

/** Runs `done` once the page and the rail stop moving after a smooth scroll. */
function whenScrollSettles(rail: HTMLElement | null, done: () => void) {
  const startedAt = performance.now();
  let last = "";
  let still = 0;
  const tick = () => {
    const position = `${window.scrollY}:${rail?.scrollLeft ?? 0}`;
    still = position === last ? still + 1 : 0;
    last = position;
    if (still >= SCROLL_SETTLE_FRAMES || performance.now() - startedAt > SCROLL_SETTLE_MAX_MS) {
      done();
      return;
    }
    requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

interface Spotlight {
  card: BenefitShowcaseCard;
  origin: HTMLElement;
}

/**
 * "Benefícios da loja": the merchant's active loyalty mechanics as cards.
 * One card fills the column; two or more scroll as a snap rail whose width
 * follows the section size. Renders nothing without an active mechanic.
 *
 * Validity is the coupon rule: a card whose weekly window is closed NOW is
 * never hidden — it is subdued and names its days, so the customer knows
 * when to come back. The browser clock is a UX hint; loyalty re-validates
 * every claim during the order.
 *
 * A clamped card opens in a spotlight: the page first scrolls it to the
 * centre, then the card itself morphs into the full view over a veil.
 */
export function BenefitsCarousel({ section }: { section: StorefrontSection }) {
  const { rounded: corners } = section;
  const active = useAtomValue(activeBenefitsAtom);
  const cards = useMemo(() => buildBenefitShowcase(active), [active]);
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const fills = useMediaQuery("(min-width: 768px)");
  const [spotlight, setSpotlight] = useState<Spotlight | null>(null);
  const opening = useRef(false);
  const reduceMotion = useReducedMotion();
  const rail = cards.length > 1;

  const expand = useCallback(
    (card: BenefitShowcaseCard, origin: HTMLElement) => {
      if (opening.current) return;
      opening.current = true;
      const open = () => setSpotlight({ card, origin });
      if (reduceMotion) {
        origin.scrollIntoView?.({ block: "nearest", inline: "nearest" });
        open();
        return;
      }
      origin.scrollIntoView?.({ behavior: "smooth", block: "center", inline: "nearest" });
      whenScrollSettles(scrollerRef.current, open);
    },
    [reduceMotion],
  );

  const closeSpotlight = useCallback(() => {
    opening.current = false;
    setSpotlight(null);
  }, []);

  if (cards.length === 0 || !active) return null;

  const rule = stackingRuleCopy(active.stackingPolicy, cards);
  const slideWidth = `${BENEFIT_WIDTH_RATIO[fills ? "desktop" : "mobile"][section.size] * 100}%`;

  return (
    <SectionRail
      label={STOREFRONT_SECTION_LABELS.benefits.label}
      titleCard={section.titleCard}
    >
      {rail ? (
        <RailScroller
          className={RAIL_COLUMN}
          scrollerClassName="snap-x snap-mandatory"
          scrollerRef={scrollerRef}
          watch={cards.length}
          step="card"
          marker="benefit-rail"
        >
          <ul className="flex items-stretch gap-3 pb-1">
            {cards.map((card) => (
              <li key={card.id} className="flex snap-start shrink-0" style={{ width: slideWidth }}>
                <BenefitCard
                  card={card}
                  size={section.size}
                  rounded={corners}
                  hidden={spotlight?.card.id === card.id}
                  onExpand={expand}
                />
              </li>
            ))}
          </ul>
        </RailScroller>
      ) : (
        <div className={RAIL_COLUMN}>
          <BenefitCard
            card={cards[0]}
            size={section.size}
            rounded={corners}
            hidden={spotlight?.card.id === cards[0].id}
            onExpand={expand}
          />
        </div>
      )}

      {rule && (
        <div className={`${RAIL_COLUMN} mt-2`}>
          <Text
            data-testid="benefit-stacking-rule"
            tone="muted"
            style={{ fontSize: `${RAIL_MICRO_REM[section.size] * 1.2}rem` }}
          >
            {rule}
          </Text>
        </div>
      )}

      {spotlight && (
        <BenefitSpotlight
          card={spotlight.card}
          origin={spotlight.origin}
          size={section.size}
          rounded={corners}
          onClosed={closeSpotlight}
        />
      )}
    </SectionRail>
  );
}
